import type { LucideIcon } from 'lucide-react'
import { TrendingUp, TrendingDown } from 'lucide-react'
import { Link } from 'react-router-dom'
import {
  Card,
  CardAction,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'

interface MetricCardProps {
  title: string
  value: number | string
  description?: string
  icon?: LucideIcon
  trend?: {
    value: number
    label: string
  }
  isLoading?: boolean
  href?: string
}

/**
 * Metric card component for displaying KPI values
 * Matches shadcn dashboard SectionCards design
 */
export function MetricCard({
  title,
  value,
  description,
  trend,
  isLoading,
  href,
}: MetricCardProps) {
  if (isLoading) {
    return (
      <Card data-testid="metric-card-skeleton">
        <CardHeader>
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-8 w-20" />
        </CardHeader>
        <CardFooter>
          <Skeleton className="h-3 w-32" />
        </CardFooter>
      </Card>
    )
  }

  const isPositiveTrend = trend && trend.value >= 0

  const card = (
    <Card className="@container/card hover:shadow-md hover:-translate-y-0.5 hover:border-primary/30 transition-[box-shadow,transform,border-color] duration-200">
      <CardHeader>
        <CardDescription>{title}</CardDescription>
        <CardTitle
          className="text-2xl font-semibold tabular-nums @[250px]/card:text-3xl"
          aria-label={`${title}: ${value}`}
        >
          {value}
        </CardTitle>
        {trend && (
          <CardAction>
            <Badge
              variant="outline"
              aria-label={`${isPositiveTrend ? 'Up' : 'Down'} ${Math.abs(trend.value)}% ${trend.label}`}
            >
              {isPositiveTrend ? (
                <TrendingUp className="size-4" />
              ) : (
                <TrendingDown className="size-4" />
              )}
              {isPositiveTrend ? '+' : ''}
              {trend.value}%
            </Badge>
          </CardAction>
        )}
      </CardHeader>
      <CardFooter className="flex-col items-start gap-1.5 text-sm">
        {description && (
          <div className="text-muted-foreground">{description}</div>
        )}
      </CardFooter>
    </Card>
  )

  if (href) {
    return (
      <Link
        to={href}
        aria-label={`${title}: ${value}`}
        className="block rounded-xl no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      >
        {card}
      </Link>
    )
  }

  return card
}
